import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { hasPermission, isPasskeyBridge, type FeatureFlags, type Permission, type MembershipRole, type PermissionSet } from '@reptree/shared'
import { supabase } from './supabase'
import { api, forgetCachedApi } from './api'
import { applyBranding } from './theme'

export interface Business {
  id: string
  name: string
  branding?: { primary_color?: string; accent_color?: string; logo_url?: string; background?: string; client_card_border?: boolean }
  /** Client-profile field schema (null → DEFAULT_PROFILE_FIELDS). */
  profile_fields?: unknown
  /** Board/workflow customization: hidden stages, on-enter actions, per-stage sections, custom stages. */
  stage_config?: {
    hidden?: string[]
    silent_stages?: string[]
    sections?: Record<string, string[]>
    custom?: { key: string; label: string; color?: string; after?: string }[]
    hide_client_activity?: boolean
    /** Public website builder feature flag (OFF by default; toggled in Customize). */
    website_enabled?: boolean
  }
  /** Default self-tape filename template ({token} grammar); null → built-in default. */
  filename_template?: string | null
  subscription_status: string
  trial_ends_at?: string | null
  /** First moment the subscription went past_due/unpaid; null when healthy. Starts the payment grace window. */
  past_due_since?: string | null
  stripe_subscription_id?: string | null
  created_at?: string
  client_can_replace?: boolean
  seats: number
}
export interface Membership {
  id: string
  business_id: string
  role: MembershipRole
  permissions: PermissionSet
  business: Business
}
interface MeData {
  /** branding = the user's PERSONAL portal-Home brand (colors/logo), independent of any agency. */
  profile: { full_name?: string; email?: string; avatar_url?: string; branding?: Business['branding']; is_testing?: boolean } | null
  memberships: Membership[]
  clients: {
    id: string
    business_id: string
    full_name: string
    /**
     * Set when this client row is a DEPENDENT the signed-in user manages as guardian; null = own profile.
     * VIEWER-RELATIVE: a co-managed adult sees their own row with this null (their portal is unchanged)
     * while their guardian's copy of the same row carries it.
     */
    managed_person_id?: string | null
    /** True when this row reaches the user via an active guardianship rather than their own login. */
    via_guardianship?: boolean
    business?: { name: string; branding?: Business['branding'] } | null
  }[]
  /** The talent has past agencies (archived, or deleted with a kept copy) — their portal is still home. */
  has_history?: boolean
  is_admin?: boolean
  /** Whether the account has proven its current email. False (with REQUIRE_EMAIL_PROOF on) shows the confirm banner. */
  email_proven?: boolean
  /**
   * Two-step sign-in state from the server (see server/src/lib/mfa.ts). Drives the enrollment prompt:
   * a suggestion when `policy` is 'suggested', a non-dismissible step when `setupRequired` is true.
   */
  mfa?: {
    hasStrongFactor: boolean
    /** The second steps on the account: 'passkey', 'totp' (an authenticator app), 'phone'. */
    methods?: ('passkey' | 'totp' | 'phone')[]
    /** Whether passkey SETUP is offered to this account (server PASSKEY_SETUP). Signing in with one always works. */
    passkeySetup?: boolean
    policy: 'required' | 'suggested'
    effectiveAt: string | null
    setupRequired: boolean
  }
  /**
   * The one-profile switches of the server that answered (env only). Record-only routes still answer 409 until
   * the mode fence agrees, so UI gated on `talentRecordMode` falls back to today's on a 409.
   */
  features?: FeatureFlags
}

interface AuthCtx {
  session: Session | null
  loading: boolean
  me: MeData | null
  /** A second step (passkey or authenticator app) is set up but this session has not passed it yet: show the gate. */
  mfaRequired: boolean
  /** Pass the gate with a 6-digit authenticator-app code, tried against the account's apps (`appIds`, from the server). */
  completeMfa: (code: string, appIds?: string[]) => Promise<void>
  /** Clear the gate after a second step succeeded elsewhere (a passkey confirmed through lib/passkey.ts). */
  finishMfa: () => Promise<void>
  activeBusinessId: string | null
  setActiveBusinessId: (id: string) => void
  activeBusiness: Business | null
  activeMembership: Membership | null
  can: (perm: Permission) => boolean
  refresh: () => Promise<void>
  signOut: () => Promise<void>
  /** Sign out on THIS device only: giving up at the two-step gate must not sign the person out everywhere. */
  signOutHere: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)
export const useAuth = (): AuthCtx => {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [me, setMe] = useState<MeData | null>(null)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [activeBusinessId, setActive] = useState<string | null>(() => localStorage.getItem('reptree-biz'))

  async function loadMe() {
    try {
      // Pending invites are no longer auto-claimed silently: they surface on the tokenless onboarding
      // screen (PendingInvites) for the signed-in, proven address, where the person accepts them.
      setMe(await api<MeData>('/me'))
    } catch {
      setMe(null)
    }
  }

  /** A second step (passkey or authenticator app) is set up but this session is still aal1: it must pass the gate
   *  before the API will talk to it. */
  async function checkMfa(): Promise<boolean> {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      return data ? data.currentLevel === 'aal1' && data.nextLevel === 'aal2' : false
    } catch {
      return false
    }
  }

  async function onSession(s: Session | null) {
    if (!s) {
      setMfaRequired(false)
      setMe(null)
      return
    }
    const needed = await checkMfa()
    setMfaRequired(needed)
    if (!needed) void loadMe()
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session)
      // Resolve the MFA check before unblocking routes — avoids a flash of aal1 requests.
      await onSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // However the session ended (here, on another device, or by Supabase), drop the worker's cached API answers.
      if (event === 'SIGNED_OUT') void forgetCachedApi()
      setSession(s)
      // Defer supabase calls out of the auth callback (SDK deadlock guard).
      setTimeout(() => void onSession(s), 0)
    })
    return () => sub.subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function completeMfa(code: string, appIds?: string[]) {
    // The account's apps as the server knows them (never the passkey bridge, whose code only the server has); without
    // that answer, the verified app factors that do not look like the bridge.
    let apps: { id: string }[] = (appIds ?? []).map((id) => ({ id }))
    if (!appIds) {
      const { data: factors, error: listErr } = await supabase.auth.mfa.listFactors()
      if (listErr) throw listErr
      apps = (factors?.totp ?? []).filter((f) => !isPasskeyBridge(f))
    }
    if (apps.length === 0) throw new Error('No authenticator is set up for this account.')
    // An account can hold more than one authenticator app, in no fixed order: the code is checked against each.
    let lastError: unknown = null
    for (const app of apps) {
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: app.id, code })
      if (!error) return finishMfa()
      lastError = error
    }
    throw lastError
  }

  async function finishMfa() {
    setMfaRequired(false)
    await loadMe()
  }

  useEffect(() => {
    if (me && me.memberships.length) {
      const valid = me.memberships.find((m) => m.business_id === activeBusinessId)
      const chosen = valid?.business_id ?? me.memberships[0]!.business_id
      if (chosen !== activeBusinessId) {
        setActive(chosen)
        localStorage.setItem('reptree-biz', chosen)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])

  const activeMembership = me?.memberships.find((m) => m.business_id === activeBusinessId) ?? null
  const activeBusiness = activeMembership?.business ?? null

  useEffect(() => {
    // Each authed shell owns its own brand — AppShell wears the active business, PortalLayout
    // wears the selected agency (or the user's personal Home brand). A dual-role user can hold
    // both, so applying here on every `me` change would clobber whichever shell is mounted.
    // We only reset to STOCK when signed out, so the public pages are never tinted.
    if (!session) applyBranding(undefined)
  }, [session])

  const value: AuthCtx = {
    session,
    loading,
    me,
    mfaRequired,
    completeMfa,
    finishMfa,
    activeBusinessId,
    setActiveBusinessId: (id) => {
      setActive(id)
      localStorage.setItem('reptree-biz', id)
    },
    activeBusiness,
    activeMembership,
    can: (perm) => (activeMembership ? hasPermission(activeMembership.role, activeMembership.permissions, perm) : false),
    refresh: loadMe,
    signOut: async () => {
      localStorage.removeItem('reptree-biz')
      await supabase.auth.signOut()
      await forgetCachedApi()
    },
    signOutHere: async () => {
      localStorage.removeItem('reptree-biz')
      await supabase.auth.signOut({ scope: 'local' })
      await forgetCachedApi()
    },
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
